/**
 * User Management - Club admin user management interface
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, CheckCircle, Users, Shield, AlertCircle, Trophy } from 'lucide-react';
import {Button, Section, Badge, FormTextArea, Card} from '../../components';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  ConfirmDialog,
  Modal
} from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import { useToast } from '../../contexts/ToastContext';

const UserManagement = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleMapping, setRoleMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all'
  });
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [showResendInviteConfirm, setShowResendInviteConfirm] = useState(false);
  const [userToResendInvite, setUserToResendInvite] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Verification states
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [userToVerify, setUserToVerify] = useState(null);
  const [verificationComment, setVerificationComment] = useState('');
  const [verifyingUserId, setVerifyingUserId] = useState(null);

  // Club admin allowed roles for user management
  const CLUB_ADMIN_ALLOWED_ROLES = ['club_admin', 'branch_admin'];

  useEffect(() => {
    loadUsersAndRoles();
  }, []);

  const loadUsersAndRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load users and roles in parallel
      const [clubUsers, clubRoles] = await Promise.all([
        apiService.getClubUsers(),
        apiService.getClubRoles(false)
      ]);

      setUsers(clubUsers);

      // Filter roles to club admin allowed roles
      const rolesData = clubRoles.roles || [];
      const filteredRoles = rolesData.filter(role => {
        const roleName = (role.name || '').toLowerCase().replace(/\s+/g, '_');
        return CLUB_ADMIN_ALLOWED_ROLES.includes(roleName);
      });
      setRoles(filteredRoles);

      const mapping = {};
      filteredRoles.forEach(role => {
        mapping[role.id] = role.name;
      });
      setRoleMapping(mapping);
    } catch (err) {
      setError(err.message);
      showError(`Error loading users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeactivateUser = async () => {
    if (!deactivateReason.trim()) {
      showError('Please provide a reason for deactivation');
      return;
    }

    try {
      await apiService.deactivateUser(selectedUser.id, deactivateReason);
      showSuccess(`User deactivated successfully`);
      setShowDeactivateModal(false);
      setDeactivateReason('');
      setSelectedUser(null);
      await loadUsersAndRoles();
    } catch (err) {
      showError(`Error deactivating user: ${err.message}`);
    }
  };

  const handleReactivateUser = async (user) => {
    try {
      await apiService.reactivateUser(user.id);
      showSuccess(`User reactivated successfully`);
      await loadUsersAndRoles();
    } catch (err) {
      showError(`Error reactivating user: ${err.message}`);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id);
      showSuccess(`User deleted successfully`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await loadUsersAndRoles();
    } catch (err) {
      showError(`Error deleting user: ${err.message}`);
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;

    try {
      const result = await apiService.resetUserPassword(userToReset.id);
      showSuccess(result.message || 'Password reset email sent successfully!');
      setShowResetPasswordConfirm(false);
      setUserToReset(null);
    } catch (err) {
      showError(`Error sending password reset: ${err.message}`);
    }
  };

  const handleResendInvitation = async () => {
    if (!userToResendInvite) return;

    try {
      const result = await apiService.resendUserInvitation(userToResendInvite.id);
      showSuccess(result.message || 'Invitation email resent successfully!');
      setShowResendInviteConfirm(false);
      setUserToResendInvite(null);
    } catch (err) {
      showError(`Error resending invitation: ${err.message}`);
    }
  };

  const handleVerifyUser = async () => {
    if (!userToVerify) return;

    try {
      setVerifyingUserId(userToVerify.id);
      const result = await apiService.verifyUser(userToVerify.id, verificationComment);
      const user_name = `${userToVerify.first_name} ${userToVerify.last_name}`;
      showSuccess(`User ${user_name} verified successfully!`);

      // Update the user in the local state immediately
      setUsers(prevUsers => prevUsers.map(u =>
        u.id === userToVerify.id
          ? { ...u, verification: result.verification }
          : u
      ));

      setShowVerifyModal(false);
      setUserToVerify(null);
      setVerificationComment('');
    } catch (err) {
      showError(`Error verifying user: ${err.message}`);
    } finally {
      setVerifyingUserId(null);
    }
  };

  const getRoleName = (roleId) => {
    if (!roleId) return 'Unknown Role';
    if (roleMapping[roleId]) return roleMapping[roleId];

    switch (roleId) {
      case 'club_admin': return 'Club Admin';
      case 'branch_admin': return 'Branch Admin';
      case 'coach': return 'Coach';
      case 'parent': return 'Parent';
      case 'platform_admin': return 'Platform Admin';
      default: return String(roleId).replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle size={20} style={{ color: '#10b981' }} />;
      case 'inactive': return <Ban size={20} style={{ color: '#ef4444' }} />;
      case 'pending': return <AlertCircle size={20} style={{ color: '#f59e0b' }} />;
      default: return <Users size={20} />;
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getRoleIcon = (roleId) => {
    switch (roleId) {
      case 'club_admin':
      case 'branch_admin':
      case 'platform_admin':
        return <Shield size={24} />;
      case 'coach': return <Trophy size={24} />;
      case 'parent': return <Users size={24} />;
      default: return <Users size={24} />;
    }
  };

  // Filter data based on current filters
  const filteredUsers = users.filter(user => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!user.first_name?.toLowerCase().includes(searchLower) &&
        !user.last_name?.toLowerCase().includes(searchLower) &&
        !user.email?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Role filter
    if (filters.role !== 'all' && user.primary_role !== filters.role) {
      return false;
    }

    // Status filter
    if (filters.status !== 'all' && user.status !== filters.status) {
      return false;
    }

    return true;
  });

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'role',
          label: 'Role',
          options: [
            { value: 'all', label: 'All Roles' },
            ...roles.map(role => ({ value: role.id, label: role.name })),
            ...(roles.length === 0 ? [
              { value: 'club_admin', label: 'Club Admin' },
              { value: 'branch_admin', label: 'Branch Admin' }
            ] : [])
          ]
        },
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'pending', label: 'Pending' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ role: filters.role, status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', role: 'all', status: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/users/add')} label="New User" />
      }
    />
  );

  if (error) {
    return (
      <AdminPageLayout
        title="USER MANAGEMENT"
        subtitle="Manage club users and access"
        backPath="/admin/club"
      >
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Error Loading Users</h2>
          <p>{error}</p>
          <Button onClick={() => loadUsersAndRoles()}>
            Retry
          </Button>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="Users"
      subtitle="Manage users and roles"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <Section title="Participant Directory">
        <Listing
          loading={loading}
          items={filteredUsers}
          actions={headerActions}
          filters={filterBar}
          renderItem={(user) => {
            const statusVariant = user.status === 'active' ? 'success' : user.status === 'pending' ? 'primary' : 'secondary';

            return (
              <ListingItem
                key={user.id}
                title={`${user.first_name} ${user.last_name}`}
                subtitle={user.email}
                icon={getRoleIcon(user.primary_role)}
                iconVariant={statusVariant}
                actions={
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <EditAction
                      size="sm"
                      onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                    />
                    {['active', 'pending'].includes(user.status) && (
                      <DeleteAction
                        size="sm"
                        onClick={() => {
                          setUserToDelete(user);
                          setShowDeleteConfirm(true);
                        }}
                        title="Delete user"
                      />
                    )}
                  </div>
                }
              >
                <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>Classification</span>
                    <span style={{ fontWeight: 600 }}>{getRoleName(user.primary_role)}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>Operational Status</span>
                    <Badge variant={statusVariant} size="sm">
                      {user.status.toUpperCase()}
                    </Badge>
                  </div>
                  {user.last_login && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>Last Synchronization</span>
                      <span style={{ fontWeight: 500 }}>{new Date(user.last_login).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Strategic Actions */}
                <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--color-border-light)', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {user.status === 'active' && (
                    <Button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowDeactivateModal(true);
                      }}
                      variant="danger"
                      size="xs"
                    >
                      Deactivate
                    </Button>
                  )}
                  {user.status === 'inactive' && (
                    <Button
                      onClick={() => handleReactivateUser(user)}
                      variant="success"
                      size="xs"
                    >
                      Reactivate Node
                    </Button>
                  )}
                  {user.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => {
                          setUserToVerify(user);
                          setVerificationComment('');
                          setShowVerifyModal(true);
                        }}
                        variant="primary"
                        size="xs"
                      >
                        Verify Credentials
                      </Button>
                      <Button
                        onClick={() => {
                          setUserToResendInvite({ id: user.id, email: user.email, name: `${user.first_name} ${user.last_name}` });
                          setShowResendInviteConfirm(true);
                        }}
                        variant="outline"
                        size="xs"
                      >
                        Resend Invitation
                      </Button>
                    </>
                  )}
                  {user.status === 'active' && !user.verification?.verified && (
                    <Button
                      onClick={() => {
                        setUserToVerify(user);
                        setVerificationComment('');
                        setShowVerifyModal(true);
                      }}
                      variant="primary"
                      size="xs"
                    >
                      Verify Credentials
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setUserToReset({ id: user.id, email: user.email });
                      setShowResetPasswordConfirm(true);
                    }}
                    variant="ghost"
                    size="xs"
                  >
                    Reset Credentials
                  </Button>
                </div>
              </ListingItem>
            );
          }}
          emptyMessage="No users found"
        />
      </Section>

      {/* Deactivate Modal */}
      <Modal
        isOpen={showDeactivateModal && !!selectedUser}
        onClose={() => setShowDeactivateModal(false)}
        title="Deactivate User"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <p style={{ margin: 0 }}>
            Are you sure you want to deactivate <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>?
            This will suspend their access to the system.
          </p>

          {selectedUser?.family_role === 'primary_parent' && (
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--color-warning-light, #fff3cd)',
              border: '1px solid var(--color-warning, #ffc107)',
              borderRadius: '4px',
              marginTop: '0.5rem'
            }}>
              <strong>⚠️ Family Deactivation Notice:</strong> This is a primary parent. Deactivating this user will also deactivate all associated family members (spouse, children, etc.). Data will be preserved and can be reactivated later.
            </div>
          )}

          <FormTextArea
            label="Rationale for Suspension"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            placeholder="Document the administrative rationale..."
            rows={3}
            required
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <Button
              onClick={() => setShowDeactivateModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeactivateUser}
              variant="danger"
              disabled={!deactivateReason.trim()}
            >
              Confirm Deactivation
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showResetPasswordConfirm}
        onClose={() => {
          setShowResetPasswordConfirm(false);
          setUserToReset(null);
        }}
        onConfirm={handleResetPassword}
        title="CREDENTIAL RESET"
        message={`Authorize password reset for ${userToReset?.email}?`}
        details="A secure credential recovery link will be dispatched to the registered email address."
        variant="info"
        confirmText="Dispatch Link"
        cancelText="Cancel"
      />

      <ConfirmDialog
        isOpen={showResendInviteConfirm}
        onClose={() => {
          setShowResendInviteConfirm(false);
          setUserToResendInvite(null);
        }}
        onConfirm={handleResendInvitation}
        title="INVITATION DISPATCH"
        message={`Resend organization invitation to ${userToResendInvite?.name}?`}
        details="This will generate a fresh invitation token and transmit it to the participant."
        variant="info"
        confirmText="Re-initialize Invitation"
        cancelText="Cancel"
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        title="DELETE USER"
        message={`Permanently delete ${userToDelete?.first_name} ${userToDelete?.last_name}?`}
        details={
          userToDelete?.family_role === 'primary_parent'
            ? "⚠️ WARNING: This is a primary parent. Deleting this user will PERMANENTLY DELETE ALL ASSOCIATED FAMILY MEMBERS (spouse, children, etc.) and cannot be undone. All data will be completely removed from the system. Use 'Deactivate' instead if you want to preserve the data."
            : "This action cannot be undone. All user data will be permanently removed from the system. Use 'Deactivate' instead if you want to preserve the data."
        }
        variant="danger"
        confirmText="Permanently Delete"
        cancelText="Cancel"
      />

      {/* Verify User Modal */}
      <Modal
        isOpen={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false);
          setUserToVerify(null);
          setVerificationComment('');
        }}
        title="Verify User"
        size="medium"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Card level={2}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.6 }}>Name</span>
                <span style={{ fontWeight: 600 }}>{userToVerify?.first_name} {userToVerify?.last_name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.6 }}>Role</span>
                <span>{getRoleName(userToVerify?.primary_role)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ opacity: 0.6 }}>Status</span>
                <Badge variant={userToVerify?.status === 'active' ? 'success' : 'primary'} size="sm">
                  {userToVerify?.status.toUpperCase()}
                </Badge>
              </div>
            </div>
          </Card>

          <FormTextArea
            label="Verification Metadata (Optional)"
            placeholder="Append administrative notes regarding credential verification..."
            value={verificationComment}
            onChange={(e) => setVerificationComment(e.target.value)}
            rows={3}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <Button
              onClick={() => {
                setShowVerifyModal(false);
                setUserToVerify(null);
                setVerificationComment('');
              }}
              variant="outline"
              disabled={verifyingUserId === userToVerify?.id}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyUser}
              variant="save"
              disabled={verifyingUserId === userToVerify?.id}
            >
              {verifyingUserId === userToVerify?.id ? 'Processing...' : 'Verify User'}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminPageLayout>
  );
};

export default UserManagement;